import { BookOpen, Check, ChevronLeft, ChevronRight, Music2 } from 'lucide-react';
import { useLayoutEffect, useState } from 'react';
import type { RoadmapLevelStage, RoadmapPiece } from 'Selectors/curriculum';
import { StageArtwork } from './StageArtwork';
import type { StagePosition } from './geometry';

/** Narrower single-row platforms show fewer labelled pieces per page. */
const FOUR_PIECE_WIDTH = 520;
const THREE_PIECE_WIDTH = 340;

function pieceProgressLabel(piece: RoadmapPiece) {
  if (piece.state === 'complete') return 'Complete';
  return piece.percentComplete ? `${Math.floor(piece.percentComplete)}% complete` : 'Not started';
}

export function RepertoireStage({
  stage,
  selectedId,
  onSelect,
  onPlay,
  onBrowse,
  position,
  compact,
  active,
  promoted,
  onOpen,
}: {
  stage: RoadmapLevelStage;
  selectedId?: number;
  onSelect: (piece: RoadmapPiece) => void;
  onPlay: (piece: RoadmapPiece) => void;
  onBrowse: () => void;
  position: StagePosition;
  /** The phone platform arranges pieces in a grid with its pager below. */
  compact: boolean;
  active: boolean;
  promoted: boolean;
  onOpen: () => void;
}) {
  const [page, setPage] = useState(0);
  const branch = stage.repertoire!;
  const pageSize =
    compact || position.width >= FOUR_PIECE_WIDTH ? 4 : position.width >= THREE_PIECE_WIDTH ? 3 : 2;
  const pages = Math.ceil(branch.pieces.length / pageSize);
  const currentPage = Math.min(page, pages - 1);
  const pieces = branch.pieces.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  useLayoutEffect(() => {
    const index = branch.pieces.findIndex((piece) => piece.piece.id === selectedId);
    if (index >= 0) setPage(Math.floor(index / pageSize));
  }, [selectedId, branch.pieces, pageSize]);
  return (
    <section
      className={`rm-stage ${active ? 'is-selected' : ''} ${promoted ? 'is-promoted' : ''} ${branch.availability === 'future' ? 'is-future' : ''}`}
      data-testid="repertoire-stage"
      data-stage-level={stage.levelNumber}
      aria-label={`Level ${stage.levelNumber} repertoire stage`}
      style={{ left: position.x, top: position.y, width: position.width, height: position.height }}
    >
      <StageArtwork />
      <button
        className="rm-stage-heading"
        data-roadmap-branch={stage.levelNumber}
        aria-label={`Explore Level ${stage.levelNumber} repertoire, ${branch.pieces.length} pieces`}
        aria-pressed={active}
        onClick={onOpen}
      >
        <BookOpen />
        <span>Repertoire</span>
        <ChevronRight />
      </button>
      <div className="rm-stage-origin">
        Level {stage.levelNumber}
        <span>·</span>
        {branch.availability === 'future' ? 'Ahead on your path' : 'Ready to explore'}
      </div>
      <div className="rm-pedestals">
        {pieces.map((piece) => (
          <button
            type="button"
            key={piece.piece.id}
            className={`rm-piece is-${piece.state} ${selectedId === piece.piece.id ? 'is-selected' : ''}`}
            data-roadmap-piece={piece.piece.id}
            aria-pressed={selectedId === piece.piece.id}
            aria-label={`Select ${piece.piece.name}, ${Math.floor(piece.percentComplete)}% complete`}
            onClick={() => onSelect(piece)}
            onDoubleClick={() => onPlay(piece)}
          >
            <span className="rm-piece-disc">
              <Music2 size={29} />
              {piece.state === 'complete' && (
                <Check className="rm-piece-check rm-hover-progress" size={17} />
              )}
            </span>
            <span className="rm-piece-name">{piece.piece.name}</span>
            <span className="rm-piece-meta">
              <span className="rm-piece-composer">
                {piece.piece.composers.map((c) => c.name).join(', ') ||
                  piece.piece.artists.map((a) => a.name).join(', ')}
              </span>
              <span className="rm-piece-progress rm-hover-progress">
                {pieceProgressLabel(piece)}
              </span>
            </span>
          </button>
        ))}
      </div>
      {pages > 1 && (
        <div className="rm-stage-pager">
          <button
            type="button"
            aria-label="Previous pieces"
            disabled={currentPage === 0}
            onClick={() => setPage(currentPage - 1)}
          >
            <ChevronLeft size={17} />
          </button>
          <span aria-live="polite">
            {currentPage + 1} / {pages}
          </span>
          <button
            type="button"
            aria-label="Next pieces"
            disabled={currentPage === pages - 1}
            onClick={() => setPage(currentPage + 1)}
          >
            <ChevronRight size={17} />
          </button>
        </div>
      )}
      <div className="rm-stage-plaque">
        <span>
          <Check size={13} />
          {branch.completedCount} of {branch.pieces.length} complete
        </span>
        <button type="button" onClick={onBrowse}>
          View all <ChevronRight size={13} />
        </button>
      </div>
    </section>
  );
}
